import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { INTEGRATIONS_MAPPING_TYPE, INTEGRATIONS_WEBHOOK_PLATFORM } from '@/constants/integrations';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useAuth } from '@/contexts/AuthProvider';
import {
  addApollo,
  getSendosoStatus,
  addSendoso,
  disconnectSendoso,
  addBynder,
  addGoogleSheets,
  addGoogleDrive,
  addHeyGen,
  addHubSpot,
  authorizeHubSpotMarketplaceInstall,
  addLinkedin,
  addNotion,
  addSalesForce,
  addVector,
  bookMeeting,
  createLinkedInPost,
  deleteWebhook,
  generateWebhookData,
  getAllCalendarsByUser,
  getAllEmailAccountsByUser,
  getBynderAssets,
  getBynderCollections,
  getCalendarIntegrations,
  getEmailIntegrations,
  getGoogleCalendarList,
  getGoogleSheetsAuthId,
  getHubSpotAuthStatus,
  getHubSpotFieldMappings,
  getHubspotFields,
  getIntegrationsByChatToken,
  listIntegrations,
  getLinkedInShareAuthStatus,
  getLinkedInShareProfile,
  disconnectLinkedInShare,
  getLinkedInPosting,
  saveLinkedInPosting,
  disconnectHubSpot,
  finalizeHubSpotMarketplaceInstall,
  getNotionDatabasesAndPages,
  getPublicCalendarData,
  getSalesforceAuthStatus,
  getSalesforceFieldMappings,
  getSalesforceFields,
  getSalesforcePersonObjectMode,
  updateSalesforcePersonObjectMode,
  getSalesforceCanvasStatus,
  getSalesforceStats,
  saveSalesforceCanvasCredentials,
  clearSalesforceCanvasCredentials,
  disconnectSalesforce,
  getSelectedBynderAssets,
  getSelectedBynderAssetsWithStatus,
  getSelectedBynderCollections,
  getSelectedBynderCollectionsWithStatus,
  getSelectedNotionPages,
  getSelectedNotionPagesWithStatus,
  getWebhook,
  saveHubSpotMappings,
  saveSalesforceMappings,
  saveSelectedBynderAssets,
  saveSelectedBynderCollections,
  saveSelectedNotionPages,
  selectGoogleCalendar,
  trainBynderCollections,
  trainNotionPages,
  upsertWebhook,
  getHeyReachCampaigns,
  getHeyReachAccounts,
  saveHeyReachApiKey,
  getHeyReachStatus,
  postHeyReachAgencyOnboarding,
  getHeyReachAgencyOnboardingStatus,
  deleteHeyReachAgencyOnboarding,
  postHeyReachAgencyInviteMember,
  getHeyReachDefaultCampaign,
  ensureHeyReachDefaultCampaign,
  getHeyReachOutreachRegions,
  updateHeyReachOutreachRegion,
  type HeyReachConnectionType,
  getHeyGenAuthStatus,
  getSixsenseAuthStatus,
  saveSixsenseApiToken,
  removeSixsenseApiToken,
  getLushaAuthStatus,
  getFullEnrichAuthStatus,
  getTregAuthStatus,
  getHunterAuthStatus,
  getSeamlessAuthStatus,
  getLeadIQAuthStatus,
  getApolloAuthStatus,
  getExaAuthStatus,
  saveExaApiKey,
  removeExaApiKey,
  getGammaAuthStatus,
  suggestFieldMappings,
} from '@/services/integrations';
import { IIntegrationWebhook, ISalesforceCanvasCredentialsBody, ISaveMappingsBody } from '@/types/integrations';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { shouldRetryHeyReachAccountsRequest } from '@/utils/heyreachConnectionHealth';

export const useGetGoogleCalendarList = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getGoogleCalendarList],
    queryFn: getGoogleCalendarList,
  });
};

export const useSelectGoogleCalendar = () => {
  return useMutation({
    mutationFn: selectGoogleCalendar,
  });
};

export const useAddSalesForce = () => {
  return useMutation({
    mutationFn: addSalesForce,
  });
};

export const useAddHubSpot = () => {
  return useMutation({
    mutationFn: addHubSpot,
  });
};

export const useAuthorizeHubSpotMarketplaceInstall = () => {
  return useMutation({
    mutationFn: authorizeHubSpotMarketplaceInstall,
  });
};

export const useFinalizeHubSpotMarketplaceInstall = () => {
  return useMutation({
    mutationFn: finalizeHubSpotMarketplaceInstall,
  });
};

export const useDisconnectHubSpot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectHubSpot,
    onSuccess: async () => {
      // Publish the disconnected state synchronously after the server confirms
      // revocation; the Integrations UI updates before background refetches.
      queryClient.setQueryData([API_ENDPOINTS.getHubSpotAuthStatus], {
        authenticated: false,
        needsReauth: false,
        needsReconnect: false,
        tokenValid: false,
        scopes: [],
        missingRequired: [],
        missingRecommended: [],
        scopeCount: 0,
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getHubSpotAuthStatus] }),
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listIntegrations] }),
      ]);
    },
  });
};

export const useAddApollo = () => {
  return useMutation({
    mutationFn: addApollo,
  });
};

/**
 * Sendoso connection for the CALLING rep (mb-server#6287) — not the workspace.
 * `configured` is false when no Sendoso credential is deployed, which is what
 * keeps the card hidden in environments that have none.
 */
export const useSendosoStatus = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getSendosoStatus],
    queryFn: getSendosoStatus,
  });
};

export const useAddSendoso = () => {
  return useMutation({
    mutationFn: addSendoso,
  });
};

export const useDisconnectSendoso = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectSendoso,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getSendosoStatus] }),
  });
};

export const useGetIntegrationsByChatToken = (chatToken: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getIntegrationsByChatToken(chatToken), chatToken],
    queryFn: () => getIntegrationsByChatToken(chatToken),
    enabled: !!chatToken,
  });
};

/**
 * Get all integrations for the current workspace (no chatToken required)
 */
export const useListIntegrations = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.listIntegrations],
    queryFn: () => listIntegrations(),
  });
};

export const useGetPublicCalendarData = (slug: string) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getPublicCalendarData(slug), slug],
    queryFn: () => getPublicCalendarData(slug),
    enabled: !!slug,
  });
};

export const useBookMeeting = () => {
  return useMutation({
    mutationFn: bookMeeting,
  });
};

const useGetEmailIntegrations = (enabled = true, options?: { writeAccessOnly?: boolean }) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getEmailIntegrations, { writeAccessOnly: options?.writeAccessOnly ?? false }],
    queryFn: () => getEmailIntegrations(options),
    enabled,
  });
};

export const useGetSalesforceFields = (type: INTEGRATIONS_MAPPING_TYPE, enabled: boolean) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getSalesforceFields, type],
    queryFn: () => getSalesforceFields(type),
    staleTime: 1000 * 60 * 2,
    enabled,
  });
};

export const useSaveSalesforceMappings = () => {
  const queryClient = useQueryClient();
  const workspace = useWorkspace();
  const workspaceId = workspace?.activeWorkspace?.vendorid;
  const fn = async (data: ISaveMappingsBody) => {
    const res = await saveSalesforceMappings(data);
    const mappingType = [
      INTEGRATIONS_MAPPING_TYPE.COMPANY,
      INTEGRATIONS_MAPPING_TYPE.PERSON,
      INTEGRATIONS_MAPPING_TYPE.ENRICHMENT,
    ].find((type) => data[type] !== undefined);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getSalesforceFieldMappings, mappingType, workspaceId],
    });
    if (
      Object.prototype.hasOwnProperty.call(data, 'userFieldDefaults')
      || Object.prototype.hasOwnProperty.call(data, 'workspaceFieldDefaults')
    ) {
      await queryClient.invalidateQueries({
        queryKey: [
          API_ENDPOINTS.getSalesforceFieldMappings,
          INTEGRATIONS_MAPPING_TYPE.PERSON,
          workspaceId,
        ],
      });
    }
    return res;
  };

  return useMutation({
    mutationFn: fn,
  });
};

export const useGetSalesforceFieldMappings = (
  type: INTEGRATIONS_MAPPING_TYPE,
  enabled: boolean,
) => {
  const workspace = useWorkspace();
  const workspaceId = workspace?.activeWorkspace?.vendorid;

  return useQuery({
    queryKey: [API_ENDPOINTS.getSalesforceFieldMappings, type, workspaceId],
    queryFn: () => getSalesforceFieldMappings(type),
    staleTime: 1000 * 60 * 2,
    enabled,
  });
};

// PRD #3 — per-vendor Salesforce person_object_mode (Contact | Lead)
export const useGetSalesforcePersonObjectMode = (enabled: boolean) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.salesforcePersonObjectMode],
    queryFn: () => getSalesforcePersonObjectMode(),
    staleTime: 1000 * 60 * 2,
    enabled,
  });
};

export const useUpdateSalesforcePersonObjectMode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (personObjectMode: 'contact' | 'lead') => updateSalesforcePersonObjectMode(personObjectMode),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.salesforcePersonObjectMode],
      });
    },
  });
};

/**
 * Salesforce integration stats (mb-server#6683).
 *
 * The server already caches per workspace for five minutes because one read spends about ten of
 * the org's Salesforce API calls; `staleTime` here matches that so a tab switch is free rather
 * than a request that would be answered from the server cache anyway. Scoped by workspace id so
 * switching workspace never shows the previous org's numbers.
 */
export const useGetSalesforceStats = (days: number, enabled: boolean) => {
  const workspace = useWorkspace();
  const workspaceId = workspace?.activeWorkspace?.vendorid;
  // The server caches per workspace and window too, so refetch() alone returns the same numbers
  // it just returned. `hardRefresh` asks it to re-read Salesforce; the flag is consumed by the
  // one request it triggers so an ordinary refetch stays cheap.
  const bypassServerCache = useRef(false);

  const query = useQuery({
    queryKey: [API_ENDPOINTS.salesforceStats, days, workspaceId],
    queryFn: () => {
      const refresh = bypassServerCache.current;
      bypassServerCache.current = false;
      return getSalesforceStats(days, refresh);
    },
    staleTime: 1000 * 60 * 5,
    // Keep the last window's numbers on screen while the next one loads, so changing the window
    // does not replace the control the user just clicked with a skeleton.
    placeholderData: (previous) => previous,
    enabled,
  });

  const hardRefresh = useCallback(() => {
    bypassServerCache.current = true;
    return query.refetch();
  }, [query]);

  return { ...query, hardRefresh };
};

// Salesforce Account Panel Canvas credentials (mb-server#5952)
export const useGetSalesforceCanvasStatus = (enabled: boolean) => {
  const workspace = useWorkspace();
  const workspaceId = workspace?.activeWorkspace?.vendorid;

  return useQuery({
    queryKey: [API_ENDPOINTS.salesforceCanvas, workspaceId],
    queryFn: () => getSalesforceCanvasStatus(),
    enabled,
  });
};

// Deliberately not a useMutation: its variables (and a failed request's config.data) would
// keep the plaintext consumer secret in the React Query cache after the form is cleared.
export const useSaveSalesforceCanvasCredentials = () => {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const save = async (body: ISalesforceCanvasCredentialsBody) => {
    setIsSaving(true);
    try {
      await saveSalesforceCanvasCredentials(body);
      await queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.salesforceCanvas] });
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
};

/**
 * Disconnect Salesforce (mb-server#6688).
 *
 * Invalidates every Salesforce-keyed query rather than only the stats, because the connection
 * the page is built on has just stopped existing: the auth status, the field mappings screen and
 * the Canvas card all describe an org this workspace no longer reaches, and leaving any of them
 * on their cached answer shows a connected integration beside the confirmation that it is gone.
 */
export const useDisconnectSalesforce = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectSalesforce(),
    onSuccess: async () => {
      await Promise.all([
        API_ENDPOINTS.salesforceStats,
        API_ENDPOINTS.getSalesforceAuthStatus,
        API_ENDPOINTS.salesforceCanvas,
        API_ENDPOINTS.getSalesforceFieldMappings,
        API_ENDPOINTS.salesforcePersonObjectMode,
      ].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
    },
  });
};

export const useClearSalesforceCanvasCredentials = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => clearSalesforceCanvasCredentials(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.salesforceCanvas] });
    },
  });
};

export const useGetHubspotFields = (type: INTEGRATIONS_MAPPING_TYPE, enabled: boolean) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getHubspotFields, type],
    queryFn: () => getHubspotFields(type),
    staleTime: 1000 * 60 * 2,
    enabled,
  });
};

export const useGetHubSpotFieldsMapping = (type: INTEGRATIONS_MAPPING_TYPE, enabled: boolean) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getHubSpotFieldMappings, type],
    queryFn: () => getHubSpotFieldMappings(type),
    staleTime: 1000 * 60 * 2,
    enabled,
  });
};

export const useSaveHubSpotMappings = () => {
  const queryClient = useQueryClient();
  const fn = async (data: ISaveMappingsBody) => {
    const res = await saveHubSpotMappings(data);
    await queryClient.invalidateQueries({
      queryKey: [API_ENDPOINTS.getHubSpotFieldMappings, Object.keys(data)[0]],
    });
    return res;
  };

  return useMutation({
    mutationFn: fn,
  });
};

export const useSuggestFieldMappings = () => {
  return useMutation({
    mutationFn: suggestFieldMappings,
  });
};

export const useGetHubSpotAuthStatus = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getHubSpotAuthStatus],
    queryFn: () => getHubSpotAuthStatus(),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount, error: unknown) => {
      // Don't retry on authentication errors
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) return false;
      // Don't retry on the enterprise-plan gate — non-enterprise workspaces will keep getting 403
      if (axiosError?.response?.status === 403) return false;
      // Don't retry on server errors
      if (axiosError?.response?.status !== undefined && axiosError.response.status >= 500) return false;
      // Retry up to 2 times for other errors (network, temporary issues)
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

export const useGetSalesforceAuthStatus = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getSalesforceAuthStatus],
    queryFn: () => getSalesforceAuthStatus(),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount, error: unknown) => {
      // Don't retry on authentication errors
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) return false;
      // Don't retry on the enterprise-plan gate — non-enterprise workspaces will keep getting 403
      if (axiosError?.response?.status === 403) return false;
      // Don't retry on server errors
      if (axiosError?.response?.status !== undefined && axiosError.response.status >= 500) return false;
      // Retry up to 2 times for other errors (network, temporary issues)
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

export const useAddLinkedin = () => {
  return useMutation({ mutationFn: addLinkedin });
};

export const useAddNotion = () => {
  return useMutation({ mutationFn: addNotion });
};

export const useGetWebhook = (platform: INTEGRATIONS_WEBHOOK_PLATFORM) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getWebhook(platform)],
    queryFn: () => getWebhook(platform),
    staleTime: 1000 * 60 * 2,
  });
};

export const useGenerateWebhookData = (
  platform: INTEGRATIONS_WEBHOOK_PLATFORM,
  onSuccess?: (data: { data: { platform: string; endpoint_url: string } }) => void,
) => {
  return useMutation({
    mutationFn: () => generateWebhookData(platform),
    onSuccess,
  });
};

export const useUpsertWebhook = (platform: INTEGRATIONS_WEBHOOK_PLATFORM) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: IIntegrationWebhook) => {
      const res = await upsertWebhook(platform, data);
      await queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWebhook(platform)],
      });

      return res;
    },
  });
};

export const useDeleteWebhook = (platform: INTEGRATIONS_WEBHOOK_PLATFORM) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await deleteWebhook(platform);
      await queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWebhook(platform)],
      });

      return res;
    },
  });
};

const useGetCalendarIntegrations = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getCalendarIntegrations],
    queryFn: getCalendarIntegrations,
  });
};

export const useGetAllCalendarsByUser = (enabled = false) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getAllCalendarsByUser],
    queryFn: getAllCalendarsByUser,
    enabled,
  });
};

/**
 * Hook that returns calendar integrations filtered by admin permissions.
 *
 * Similar to useGetFilteredEmailIntegrations, this hook implements user-specific
 * visibility controls for calendar integrations based on workspace admin permissions.
 *
 * @returns {object} React Query result object with filtered calendar integrations
 * @returns {object} returns.data - The filtered calendar integrations data
 * @returns {ICalendarByVendorItem[]} returns.data.calendarIntegrations - Array of calendar integration items
 * @returns {boolean} returns.isLoading - Loading state from appropriate query
 * @returns {boolean} returns.isError - Error state from appropriate query
 * @returns {Error|null} returns.error - Error object if any
 *
 * @example
 * ```tsx
 * const { data: calendarIntegrationsData, isLoading } = useGetFilteredCalendarIntegrations();
 * const calendars = calendarIntegrationsData?.calendarIntegrations || [];
 * ```
 *
 * @behavior
 * - **Admin users**: See all workspace calendar integrations
 * - **Non-admin users**: See only their own calendar accounts
 */
export const useGetFilteredCalendarIntegrations = () => {
  const workspace = useWorkspace();
  const isAdmin = workspace?.isAdmin || false;

  const workspaceCalendarsQuery = useGetCalendarIntegrations();
  const userCalendarsQuery = useGetAllCalendarsByUser(!isAdmin);

  // Return appropriate query based on admin status
  if (isAdmin) {
    return {
      ...workspaceCalendarsQuery,
      data: {
        calendarIntegrations: workspaceCalendarsQuery.data?.calendarIntegrations || []
      }
    };
  }

  return {
    ...userCalendarsQuery,
    data: {
      calendarIntegrations: userCalendarsQuery.data || []
    }
  };
};

export const useGetAllEmailAccountsByUser = (enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getAllEmailAccountsByUser],
    queryFn: getAllEmailAccountsByUser,
    enabled,
  });
};

/**
 * Hook that returns email integrations with configurable visibility scope.
 *
 * Default behavior preserves existing semantics:
 * - admins see workspace mailboxes
 * - non-admins see only their own mailboxes
 *
 * Play builder can opt into `scope: 'workspace'` so saved mailbox selections
 * from teammate-owned workflows still render correctly for non-admin viewers.
 *
 * @returns {object} React Query result object with filtered email integrations
 * @returns {object} returns.data - The filtered email integrations data
 * @returns {IEmailIntegrationItem[]} returns.data.emailIntegrations - Array of email integration items
 * @returns {boolean} returns.isLoading - Loading state from appropriate query
 * @returns {boolean} returns.isError - Error state from appropriate query
 * @returns {Error|null} returns.error - Error object if any
 *
 * @example
 * ```tsx
 * const { data: emailIntegrationsData, isLoading } = useGetFilteredEmailIntegrations();
 *
 * const mailboxOptions = emailIntegrationsData?.emailIntegrations?.map(integration => ({
 *   value: integration.id,
 *   label: `${integration.first_name} ${integration.last_name} <${integration.email}>`
 * })) || [];
 * ```
 *
 * @behavior
 * - `scope: 'workspace'` -> all workspace mailboxes via `list-email-integrations`
 * - `scope: 'user'` -> only the current user's mailboxes via `get-all-email-accounts-by-user`
 * - `scope: 'auto'` -> workspace for admins, user-only for non-admins
 *
 * @dependencies
 * - {@link useWorkspace} - For admin detection when `scope: 'auto'`
 * - {@link useGetEmailIntegrations} - Workspace-wide email integrations
 * - {@link useGetAllEmailAccountsByUser} - User-specific email accounts
 *
 * @see {@link IEmailIntegrationItem} for data structure details
 * @see {@link useWorkspace} for admin permission context
 */
export const useGetFilteredEmailIntegrations = (options?: {
  writeAccessOnly?: boolean;
  scope?: 'auto' | 'workspace' | 'user';
}) => {
  const workspace = useWorkspace();
  const isAdmin = workspace?.isAdmin || false;
  const scope = options?.scope || 'auto';
  const useWorkspaceScope = scope === 'workspace' || (scope === 'auto' && isAdmin);
  const useUserScope = scope === 'user' || (scope === 'auto' && !isAdmin);

  const workspaceEmailsQuery = useGetEmailIntegrations(useWorkspaceScope, options);
  const userEmailsQuery = useGetAllEmailAccountsByUser(useUserScope);

  if (useWorkspaceScope) {
    return {
      ...workspaceEmailsQuery,
      data: {
        emailIntegrations: workspaceEmailsQuery.data?.emailIntegrations || [],
      },
    };
  }

  return {
    ...userEmailsQuery,
    data: {
      emailIntegrations: userEmailsQuery.data || [],
    },
  };
};

export const useGetNotionDatabasesAndPages = () => {
  return useMutation({
    mutationFn: getNotionDatabasesAndPages,
  });
};

export const useSaveSelectedNotionPages = () => {
  return useMutation({
    mutationFn: saveSelectedNotionPages,
  });
};

export const useGetSelectedNotionPages = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getSelectedNotionPages],
    queryFn: getSelectedNotionPages,
  });
};

export const useGetSelectedNotionPagesWithStatus = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getSelectedNotionPagesWithStatus],
    queryFn: getSelectedNotionPagesWithStatus,
  });
};

export const useTrainNotionPages = () => {
  return useMutation({
    mutationFn: trainNotionPages,
  });
};

export const useAddBynder = () => {
  return useMutation({
    mutationFn: addBynder,
  });
};

const useGetBynderCollections = () => {
  const [queryParams, setQueryParams] = useState<{
    query?: string;
    page?: number;
  }>({
    page: 1,
  });

  return {
    ...useQuery({
      queryKey: [API_ENDPOINTS.getBynderCollections, queryParams],
      queryFn: () => getBynderCollections(queryParams),
      placeholderData: keepPreviousData,
    }),
    queryParams,
    setQueryParams,
  };
};

const useSaveSelectedBynderCollections = () => {
  return useMutation({
    mutationFn: saveSelectedBynderCollections,
  });
};

export const useGetSelectedBynderCollections = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getSelectedBynderCollections],
    queryFn: getSelectedBynderCollections,
  });
};

export const useGetSelectedBynderCollectionsWithStatus = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getSelectedBynderCollectionsWithStatus],
    queryFn: getSelectedBynderCollectionsWithStatus,
  });
};

export const useTrainBynderCollections = () => {
  return useMutation({
    mutationFn: trainBynderCollections,
  });
};

type BynderAssetsResponse = {
  media: Array<{
    id: string;
    name: string;
    description: string;
    extension: string[];
    property_Pillar?: string[];
  }>;
  total: { count: number };
  page?: number;
};

export const useGetBynderAssets = () => {
  const [queryParams, setQueryParams] = useState<{ query?: string; page?: number }>({ page: 1 });

  return {
    ...useQuery<BynderAssetsResponse>({
      queryKey: [API_ENDPOINTS.getBynderAssets, queryParams],
      queryFn: () => getBynderAssets(queryParams),
      placeholderData: keepPreviousData,
    }),
    queryParams,
    setQueryParams,
  };
};

export const useSaveSelectedBynderAssets = () => {
  return useMutation({
    mutationFn: saveSelectedBynderAssets,
  });
};

export const useGetSelectedBynderAssets = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getSelectedBynderAssets],
    queryFn: getSelectedBynderAssets,
  });
};

const useGetSelectedBynderAssetsWithStatus = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getBynderAssetsWithStatus],
    queryFn: getSelectedBynderAssetsWithStatus,
  });
};

export const useAddHeyGen = () => {
  return useMutation({
    mutationFn: addHeyGen,
  });
};

export const useAddGoogleSheets = () => {
  return useMutation({
    mutationFn: addGoogleSheets,
  });
};

export const useAddGoogleDrive = () => {
  return useMutation({
    mutationFn: addGoogleDrive,
  });
};

export const useGetGoogleSheetsAuthId = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getGoogleSheetsAuthId],
    queryFn: getGoogleSheetsAuthId,
  });
};

export const useAddVector = () => {
  return useMutation({
    mutationFn: addVector,
  });
};

export const useGetHeyReachCampaigns = (enabled = true) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getHeyReachCampaigns],
    queryFn: getHeyReachCampaigns,
    enabled: isAuthenticated && enabled,
  });
};

export const useGetHeyReachAccounts = (
  enabled = true,
  connectionType?: HeyReachConnectionType,
  purpose?: 'delivery',
) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getHeyReachAccounts, connectionType, purpose],
    queryFn: () => getHeyReachAccounts(connectionType, purpose),
    enabled: isAuthenticated && enabled,
    retry: shouldRetryHeyReachAccountsRequest,
    refetchOnWindowFocus: isAuthenticated && enabled,
  });
};

export const useAddHeyReach = () => {
  return useMutation({
    mutationFn: saveHeyReachApiKey,
  });
};

export const useGetHeyReachStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getHeyReachStatus],
    queryFn: getHeyReachStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// HeyReach agency onboarding (mb-frontend#1730).
// `enabled` is gated by both the user being authenticated AND the modal being
// open — closing the modal stops the 15s polling without unmounting consumers.
export const useGetHeyReachAgencyOnboardingStatus = (enabled = true) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getHeyReachAgencyOnboardingStatus],
    queryFn: getHeyReachAgencyOnboardingStatus,
    enabled: isAuthenticated && enabled,
    // Stop polling once LinkedIn is connected — terminal state needs no further polls.
    refetchInterval: (query) => {
      if (!enabled) return false;
      if (query.state.data?.state === 'linkedin_connected') return false;
      return 15_000;
    },
    refetchOnWindowFocus: enabled,
    refetchOnReconnect: enabled,
    staleTime: 0,
  });
};

// MarketBetter-managed default delivery campaign readiness (#2004).
// Polls while it is still provisioning ('creating') so the UI flips to
// 'ready' without a manual refresh; stops polling once terminal.
export const useGetHeyReachDefaultCampaign = (enabled = true) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.heyReachDefaultCampaign],
    queryFn: getHeyReachDefaultCampaign,
    enabled: isAuthenticated && enabled,
    refetchInterval: (query) => (
      query.state.data?.status === 'creating' ? 5_000 : false
    ),
    refetchOnWindowFocus: enabled,
    staleTime: 0,
  });
};

export const useEnsureHeyReachDefaultCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ensureHeyReachDefaultCampaign,
    onSuccess: (data) => {
      queryClient.setQueryData([API_ENDPOINTS.heyReachDefaultCampaign], data);
    },
  });
};

/**
 * Shared by every sender row, so the workspace's rules are fetched once rather than once per
 * seat: the endpoint returns the whole workspace and each row filters to its own id.
 *
 * `enabled` exists because the endpoint is workspace-admin only — a member rendering the same
 * rows would otherwise generate a 403 per mount.
 */
export const useHeyReachOutreachRegions = (enabled = true) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [API_ENDPOINTS.getHeyReachOutreachRegions],
    queryFn: getHeyReachOutreachRegions,
    enabled: isAuthenticated && enabled,
  });
};

export const useUpdateHeyReachOutreachRegion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, allowedCountryCodes }: {
      accountId: number;
      allowedCountryCodes: string[];
    }) => updateHeyReachOutreachRegion(accountId, allowedCountryCodes),
    // Invalidate rather than patch the cache: an empty list DELETEs the row, so the saved
    // shape is an absent entry, not an entry with an empty array.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getHeyReachOutreachRegions] });
    },
  });
};

export const usePostHeyReachAgencyOnboarding = () => {
  return useMutation({
    mutationFn: (workspaceName?: string) => postHeyReachAgencyOnboarding(workspaceName),
  });
};

export const useDeleteHeyReachAgencyOnboarding = () => {
  return useMutation({
    mutationFn: deleteHeyReachAgencyOnboarding,
  });
};

// Invite somebody to an already-provisioned managed workspace. No argument invites the
// logged-in user (mb-frontend / mb-server#1926, used by additional org members); passing
// `{ targetUserId }` invites that teammate and is admin-only on the server
// (mb-server#5522).
export const usePostHeyReachAgencyInviteMember = () => {
  return useMutation({
    mutationFn: postHeyReachAgencyInviteMember,
  });
};

export const useGetHeyGenAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['heygen-auth-status'],
    queryFn: getHeyGenAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// 6sense Integration Hooks (GH#750)
export const useGetSixsenseAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['sixsense-auth-status'],
    queryFn: getSixsenseAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

const useSaveSixsenseApiToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveSixsenseApiToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sixsense-auth-status'] });
    },
  });
};

const useRemoveSixsenseApiToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeSixsenseApiToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sixsense-auth-status'] });
    },
  });
};

// Lusha Integration Hooks — email + phone enrichment
export const useGetLushaAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['lusha-auth-status'],
    queryFn: getLushaAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// FullEnrich Integration Hooks — email + phone enrichment
export const useGetFullEnrichAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['fullenrich-auth-status'],
    queryFn: getFullEnrichAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// Treg Integration Hooks — Apollo/Lusha catalog proxy
export const useGetTregAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['treg-auth-status'],
    queryFn: getTregAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// Hunter Integration Hooks — email finding + verification
export const useGetHunterAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['hunter-auth-status'],
    queryFn: getHunterAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// LeadIQ Integration Hooks — work email + direct phone
export const useGetLeadIQAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['leadiq-auth-status'],
    queryFn: getLeadIQAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// Apollo Integration Hooks — OAuth-backed BYO enrichment provider
export const useGetApolloAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['apollo-auth-status'],
    queryFn: getApolloAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// Exa Integration Hooks
export const useGetExaAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['exa-auth-status'],
    queryFn: getExaAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2,
  });
};

export const useSaveExaApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveExaApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exa-auth-status'] });
    },
  });
};

export const useRemoveExaApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeExaApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exa-auth-status'] });
    },
  });
};

// Gamma Integration Hooks — presentation generation
export const useGetGammaAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['gamma-auth-status'],
    queryFn: getGammaAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2,
  });
};

// LinkedIn Share Integration Hooks (GH#804)
export const useGetLinkedInShareAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['linkedin-share-auth-status'],
    queryFn: getLinkedInShareAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

export const useGetLinkedInShareProfile = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['linkedin-share-profile'],
    queryFn: getLinkedInShareProfile,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useDisconnectLinkedInShare = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectLinkedInShare,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin-share-auth-status'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-share-profile'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-posting'] });
    },
    // A refusal means this page is out of date, so show what is true now.
    onError: () => queryClient.invalidateQueries({ queryKey: ['linkedin-posting'] }),
  });
};

// Scoped to the workspace: a switch in another tab must not leave this tab showing the old
// workspace's account while its requests go to the new one.
export const useLinkedInPosting = ({ enabled }: { enabled: boolean }) => {
  const { isAuthenticated } = useAuth();
  const vendorId = useWorkspace()?.activeWorkspace?.vendorid;

  return useQuery({
    queryKey: ['linkedin-posting', vendorId],
    queryFn: getLinkedInPosting,
    enabled: isAuthenticated && enabled && !!vendorId,
    staleTime: 1000 * 60,
  });
};

export const useSaveLinkedInPosting = () => {
  const queryClient = useQueryClient();
  const vendorId = useWorkspace()?.activeWorkspace?.vendorid;

  return useMutation({
    mutationFn: saveLinkedInPosting,
    onSuccess: (status) => queryClient.setQueryData(['linkedin-posting', vendorId], status),
    // A refusal usually means this page is out of date, so show what is true now.
    onError: () => queryClient.invalidateQueries({ queryKey: ['linkedin-posting'] }),
  });
};

export const useCreateLinkedInPost = () => {
  return useMutation({
    mutationFn: createLinkedInPost,
  });
};

// Seamless Integration Hooks — email finding + verification
export const useGetSeamlessAuthStatus = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['seamless-auth-status'],
    queryFn: getSeamlessAuthStatus,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};
